import Vue from 'vue'
import Vuex from 'vuex'

import _ from 'lodash'
import _uniqBy from 'lodash/uniqBy'
import Promise from 'bluebird'

import nluApi from '../api/nlu'

import test from './modules/test'
import versions from './modules/versions'
import statuses from './modules/statuses'
import autogenerated from './modules/autogenerated'
import entities from './modules/entities'
import patterns from './modules/patterns'
import features from './modules/features'
import utterances from './modules/utterances'
import prebuiltModels from './modules/prebuilt-models'
import notifications from './modules/notifications'
import customModels from './modules/custom-models'
import auth from './modules/auth'
import flows from './modules/flows'
import deployments from './modules/deployments'
import users from './modules/users'
import files from './modules/files'

const PAGE_SIZE = 15

Vue.use(Vuex)
const store = new Vuex.Store({})
store.registerModule('auth', auth)
store.registerModule('knowledgebase', {
  namespaced: true,

  modules: {
    users,
    deployments,
    flows,
    files,
    test,
    versions,
    statuses,
    autogenerated,
    entities,
    patterns,
    features,
    utterances,
    prebuiltModels,
    notifications,
    customModels
  },

  state: {
    appIsLoading: true,
    migrateBtnProcessing: false,
    current: '',
    knowledgeBases: [],
    limits: { KNOWLEDGE_BASES: { ENTITY_EXTRACTION_KM_NUMBER: 10 } },
    allApplications: [],
    categories: {
      loading: true,
      value: [],
      searchText: '',
      last: '',
      isTruncated: true
    },
    intents: {
      loading: true,
      value: []
    }
  },

  getters: {
    isCurrentKMWithEntityExtractions (state, getters) {
      const currentModel = getters.getCurrent

      return !!(currentModel && currentModel.extensions && currentModel.extensions.includes('luis'))
    },

    getCurrent (state) {
      return _.find([...state.knowledgeBases, ...state.customModels.customModels], ['id', state.current])
    },

    isTableLoading (state) {
      return state.categories.loading ||
          state.autogenerated.suggested.loading ||
          state.autogenerated.unrecognized.loading ||
          state.entities.allPrebuiltEntities.loading ||
          state.entities.allEntities.loading
    }
  },

  actions: {
    _updateModelModifiedDate ({ state, commit }) {
      const modifiedDate = _.floor(Date.now() / 1000)
      const updatedModels = _.map(state.knowledgeBases,
        (el) => ((el.id === state.current) ? { ...el, modifiedDate } : el))

      commit('SET_MODELS', updatedModels)
    },

    async initNluApplication ({ commit, dispatch }) {
      commit('SET_APP_LOADER', true)
      await Promise.all([
        dispatch('getLimits', 'KNOWLEDGE_BASES.ENTITY_EXTRACTION_KM_NUMBER'),
        dispatch('listAllModels'),
        dispatch('customModels/fetchCustomModels'),
        dispatch('customModels/fetchCustomEngines')
      ])
      commit('SET_APP_LOADER', false)
    },

    async initNluModel ({ commit, dispatch }, id) {
      commit('SET_MODEL_ID', id)
      dispatch('notifications/clearNotifications')
      dispatch('statuses/setModelStatuses')

      await Promise.all([
        dispatch('fetchCategories'),
        dispatch('fetchAllApplications'),
        dispatch('autogenerated/fetchAutogenerated', { type: 'suggested', firstLoad: true }),
        dispatch('autogenerated/fetchAutogenerated', { type: 'unrecognized', firstLoad: true }),
        dispatch('entities/fetchAllPrebuiltEntities'),
        dispatch('entities/fetchAllEntities')
      ])
    },

    async createKnowledgeBase ({ dispatch }, data) {
      try {
        await nluApi.createKnowledgeBase(data)
      } catch (error) {
        dispatch(
          'notifications/addError',
          { titleLabel: 'store.index.createKnowledgeBase.title', error }
        )
      }
    },

    async deleteKnowledgeModel ({ state, getters, dispatch }, { id }) {
      try {
        if (!id) {
          console.error('deleteKnowledgeModel:', id)
          throw new Error('id is not defined')
        }
        await nluApi.deleteKnowledgeBase({ id })
        dispatch('listAllModels')
      } catch (error) {
        dispatch(
          'notifications/addError',
          { titleLabel: 'store.index.deleteKnowledgeModel.title', error }
        )
      }
    },

    async importJsonModel ({ getters }, data) {
      return await nluApi.importJson(data)
    },

    async importModel ({ getters }, { type, ...data }) {
      switch (type) {
        case 'csv':
          return await nluApi.importCsv(data)
        case 'json':
          return await nluApi.import(data)
        default:
          throw new Error(`Unsupported model type: ${type}`)
      }
    },

    async exportKnowledgeBase ({ state, getters, dispatch }, { id }) {
      try {
        if (!id) {
          console.error('exportKnowledgeBase:', id)
          throw new Error('id is not defined')
        }
        return await nluApi.export({ kbId: id })
      } catch (error) {
        dispatch(
          'notifications/addError',
          { titleLabel: 'store.index.exportKnowledgeBase.title', error }
        )
      }
    },

    async updateNluModelSettings ({
      state, getters, commit, dispatch
    }, updatedSettings) {
      try {
        const id = state.current
        const updatedNluModel = await nluApi.updateKnowledgeBase({ ...updatedSettings, id })
        commit('UPDATE_KNOWLEDGEBASE', { id, updatedModel: updatedNluModel })
      } catch (error) {
        dispatch(
          'notifications/addError',
          { titleLabel: 'store.index.updateNluModelSettings.title', error }
        )
      }
    },

    async listAllModels ({ getters, commit, dispatch }) {
      try {
        const response = await nluApi.listAllKnowledgeBases()
        commit('SET_MODELS', response)
        dispatch('statuses/setModelStatuses')
      } catch (error) {
        dispatch(
          'notifications/addError',
          { titleLabel: 'store.index.listAllModels.title', error }
        )
      }
    },

    async fetchAllApplications ({ state, getters, commit }) {
      const response = await nluApi.listAllApplications({
        kbId: state.current,
        projection: JSON.stringify(['id', 'name', 'luisId'])
      })
      commit('SET_ALL_APPLICATIONS', response)
    },

    async getLimits ({ commit }, path) {
      const response = await nluApi.getLimits(path)
      commit('SET_LIMITS', { path, response })
    },

    async fetchCategories ({ state, getters, commit, dispatch }, searchText = '') {
      try {
        commit('SET_CATEGORIES', { value: [], loading: true })
        const { items, last } = await nluApi.listApplications({
          kbId: state.current,
          text: searchText,
          limit: PAGE_SIZE,
          projection: JSON.stringify([state.patterns.isPatternView ? 'patterns' : 'utterances'])
        })
        const categories = _.map(items, (item) => ({ ...item, loading: false }))
        if (state.patterns.isPatternView) {
          const patterns = _
            .chain(categories)
            .flatMap((cat) => _.map(cat.patterns, (pattern) => ({ ...pattern, appId: cat.id })))
            .compact()
            .map((el) => ({ ...el, loading: false }))
            .value()

          commit('patterns/SET_PATTERNS', { value: patterns })
        } else {
          const utterances = _
            .chain(categories)
            .flatMap('utterances')
            .compact()
            .concat(state.autogenerated.suggested.value, state.autogenerated.unrecognized.value)
            .map((el) => ({ ...el, checked: false, loading: false }))
            .value()

          commit('utterances/SET_UTTERANCES', { value: utterances })
        }
        const intents = _.flatMap(categories, (el) => _
          .map(el.intents, (e) => ({ ...e, category: el.id, loading: false })))

        commit('SET_CATEGORIES', { value: categories, last })
        commit('SET_INTENTS', { value: intents })
      } catch (error) {
        dispatch(
          'notifications/addError',
          { titleLabel: 'store.index.fetchCategories.title', error }
        )
        commit('SET_CATEGORIES', { value: [] })
        commit('utterances/SET_UTTERANCES', {})
        commit('SET_INTENTS', {})
      }
    },

    async fetchNextCategoriesPage ({ state, getters, commit, dispatch }) {
      try {
        const { items, last } = await nluApi.listApplications({
          kbId: state.current,
          limit: PAGE_SIZE,
          last: state.categories.last
        })
        if (items.length) {
          const categories = items.map(obj => ({ ...obj, loading: false }))
          const utterances = categories.flatMap(ctg => {
            return ctg.utterances.map(obj => ({ ...obj, checked: false, loading: false }))
          })
          const intents = categories.flatMap(ctg => {
            return ctg.intents.map(intent => ({ ...intent, category: ctg.id, lodaing: false }))
          })
          const allCategories = _uniqBy(state.categories.value.concat(categories), 'id')
          const allIntents = _uniqBy(state.intents.value.concat(intents), 'id')
          const allUtterances = utterances.concat(state.utterances.value)

          commit('SET_CATEGORIES', { value: allCategories, isTruncated: true, last })
          commit('utterances/SET_UTTERANCES', { value: allUtterances })
          commit('SET_INTENTS', { value: allIntents })
        } else {
          commit('SET_CATEGORIES', { isTruncated: false })
        }
      } catch (error) {
        dispatch('notifications/addError', {
          titleLabel: 'store.index.fetchNextCategoriesPage.title',
          error
        })
        commit('SET_CATEGORIES', { value: [] })
        commit('utterances/SET_UTTERANCES', {})
        commit('SET_INTENTS', {})
      }
    },

    async createPairsCsv ({ state, rootState, getters, commit, dispatch }, { file, contextName }) {
      try {
        const prefix = `${rootState.auth.userId}/${Date.now()}_`
        const fileModel = { name: file.name, type: file.type, file }
        const key = `${prefix}${file.name}`
        await dispatch('files/uploadFile', { prefix, fileModel, isPublic: true })
        const downloadUrl = await dispatch('files/getDownloadUrl', { key: key, isPublic: true })
        const request = {
          fileUrl: downloadUrl,
          kbId: state.current
        }
        if (contextName) {
          request.applicationName = contextName
        }
        await nluApi.createPairsCsv(request)
        dispatch(
          'notifications/addInfo',
          {
            titleLabel: 'store.index.createPairsCsv.title',
            msgLabel: 'store.index.createPairsCsv.successMsg'
          }
        )
        dispatch('listAllModels')
        dispatch('fetchCategories')
        dispatch('entities/fetchAllEntities')
      } catch (error) {
        console.debug('Wasnt able to create pair from csv:', error)
        let errorMessage = error.message
        if (error.isAxiosError) {
          errorMessage = error.response.data ? error.response.data.message : undefined
        }
        dispatch('notifications/addError', { titleLabel: 'store.index.createPairsCsv.title', error: errorMessage })
        throw error
      }
    },

    async createPair ({ state, getters, commit, dispatch }, {
      applicationName,
      intentName,
      intentType,
      utteranceText,
      target,
      customData
    }) {
      try {
        const response = await nluApi.createPair({
          kbId: state.current,
          applicationName,
          intentName: _.trim(intentName),
          intentType,
          utteranceText: _.trim(utteranceText),
          customData
        })
        dispatch('_updateModelModifiedDate')
        const contextIsExist = _.includes(_.map(_.get(state, 'categories.value', []), 'name'), applicationName)
        if (!contextIsExist) {
          const newCategorie = _.cloneDeep(response.application)
          delete newCategorie.kbId
          _.set(newCategorie, 'utterances', [_.get(response, 'intent')])
          _.set(newCategorie, 'intents', [_.get(response, 'intent')])
          _.set(newCategorie, 'loading', false)

          commit('SET_CATEGORIES', { value: [newCategorie, ...state.categories.value] })

          const newIntent = { ..._.get(response, 'intent'), appId: response.application.id }
          commit('SET_INTENTS', { value: [newIntent, ...state.intents.value] })

          commit('utterances/SET_UTTERANCES', { value: [_.get(response, 'utterance'), ...state.utterances.value] })
        } else if (target !== 'utterance') {
          const intentsInContext = _.chain(_.get(state, 'categories.value'))
            .find(['name', applicationName])
            .get('intents')
            .map('name')
            .value()

          if (_.includes(intentsInContext, intentName)) {
            commit('utterances/SET_UTTERANCES', { value: [_.get(response, 'utterance'), ...state.utterances.value] })
          } else {
            const newIntent = _.get(response, 'intent')
            _.set(newIntent, 'category', newIntent.application)

            commit('SET_INTENTS', { value: [_.get(response, 'intent'), ...state.intents.value] })
            commit('utterances/SET_UTTERANCES', { value: [_.get(response, 'utterance'), ...state.utterances.value] })
          }
        }

        if (target === 'utterance') {
          const updatedUtterancesValue = [response.utterance, ...state.utterances.value]
          commit('utterances/SET_UTTERANCES', { value: updatedUtterancesValue })

          if (_.get(getters, 'statuses/productionTrainStatus') === 'READY') {
            await dispatch('utterances/autoPredictUtteranceEntities', {
              utterance: response.utterance,
              appId: response.utterance.application
            })
          }
        }

        return { ...response.utterance, intentType: response.intent.type, application: response.application.name, intent: response.intent.name }
      } catch (error) {
        let msg
        if (error.response && error.response.data) {
          msg = error.response.data.message
        }
        dispatch(
          'notifications/addError',
          { titleLabel: 'store.index.createPair.title', error: msg || error }
        )
      }
    },

    async updateContext ({
      state, commit, dispatch, getters
    }, data) {
      try {
        const response = await nluApi.updateApplication({ kbId: state.current, ...data })
        const categories = _.map(state.categories.value, (item) => (item.id === response.id
          ? {
              ...item,
              name: response.name,
              model_features: response.model_features
            }
          : item
        ))

        commit('SET_CATEGORIES', { value: categories })
        dispatch('_updateModelModifiedDate')
        return Promise.resolve()
      } catch (error) {
        dispatch(
          'notifications/addError',
          { titleLabel: 'store.index.updateContext.title', error }
        )
      }
    },

    async deleteCategory ({
      state, commit, dispatch, getters
    }, { appId }) {
      let loadingOff
      try {
        const loadingOn = _.map(state.categories.value, (obj) => (obj.id === appId ? { ...obj, loading: true } : obj))
        commit('SET_CATEGORIES', { value: loadingOn })
        await nluApi.deleteApplication({ appId, kbId: state.current })
        loadingOff = _.reject(state.categories.value, ['id', appId])
        commit('SET_CATEGORIES', { value: loadingOff })
        dispatch('statuses/fetchStatus')
      } catch (error) {
        commit('SET_CATEGORIES', { value: loadingOff })
        dispatch(
          'notifications/addError',
          { titleLabel: 'store.index.deleteCategory.title', error }
        )
      }
    },

    async updateIntent ({ state, getters, commit, dispatch }, value) {
      const loadingOn = _.map(state.intents.value, (obj) => (obj.id === value.id ? { ...obj, loading: true } : obj))

      commit('SET_INTENTS', { loading: true, value: loadingOn })
      try {
        const appId = value.appId || value.category
        const res = await nluApi.updateIntent({
          ..._.omit(value, 'utterances'),
          appId,
          kbId: state.current,
          postprocessFlows: value.processFlows
        })
        const updatedIntents = _.map(state.intents.value, (obj) => (obj.id === value.id
          ? {
              name: res.name,
              type: res.type,
              id: res.id,
              category: res.application,
              customData: res.customData,
              loading: false,
              postprocessFlows: res.postprocessFlows,
              features: res.features
            }
          : obj
        ))

        commit('SET_INTENTS', { loading: false, value: updatedIntents })
        dispatch('statuses/fetchStatus')
        dispatch('_updateModelModifiedDate')
      } catch (error) {
        dispatch(
          'notifications/addError',
          { titleLabel: 'store.index.updateIntent.title', error }
        )
        const loadingOff = _.map(state.intents.value, (obj) => (obj.id === value.id ? { ...obj, loading: false } : obj))

        commit('SET_INTENTS', { loading: true, value: loadingOff })
      }
    },

    async deleteIntent ({ state, commit, getters, dispatch }, { id, appId }) {
      try {
        const loadingOn = _.map(state.intents.value, (obj) => (obj.id === id
          ? { ...obj, loading: true }
          : obj
        ))
        commit('SET_INTENTS', { loading: true, value: loadingOn })
        await nluApi.deleteIntent({ id, appId, kbId: state.current })
        const loadingOff = _.reject(state.intents.value, ['id', id])
        commit('SET_INTENTS', { loading: false, value: loadingOff })
        dispatch('statuses/fetchStatus')
      } catch (error) {
        dispatch('notifications/addError', { titleLabel: 'store.index.deleteIntent.title', error })
      }
    },

    async migrateKb ({ state, getters, commit, dispatch }, newName) {
      try {
        commit('SET_MIGRATE_BTN_PROCESSING', true)
        await nluApi.clone({ kbId: state.current, newName })
      } catch (error) {
        dispatch('notifications/addError', { titleLabel: 'store.index.migrateKb.title', error })
      }
      commit('SET_MIGRATE_BTN_PROCESSING', false)
    },

    // data - {type, name, regexPattern}
    async createEntity ({ state, getters, dispatch }, data) {
      try {
        dispatch('_updateModelModifiedDate')
        const newEntity = await nluApi.createEntity({ kbId: state.current, ...data })

        await Promise.all([
          dispatch('entities/fetchAllEntities'),
          dispatch('fetchCategories'),
          dispatch('listAllModels')
        ])

        return newEntity
      } catch (error) {
        dispatch('notifications/addError', { titleLabel: 'store.index.createEntity.title', error })
      }
    },

    async updateEntity ({ state, getters, dispatch }, data) {
      try {
        dispatch('_updateModelModifiedDate')
        await nluApi.updateEntity({ kbId: state.current, ..._.omit(data, ['prebuiltModelId']) })

        return await Promise.all([
          dispatch('entities/fetchAllEntities'),
          dispatch('fetchCategories'),
          dispatch('listAllModels')
        ])
      } catch (error) {
        dispatch('notifications/addError', { titleLabel: 'store.index.updateEntity.title', error })
      }
    }
  },

  mutations: {
    SET_APP_LOADER (state, value) {
      state.appIsLoading = value
    },

    SET_SEARCH_TEXT (state, text) {
      state.searchText = text
    },

    SET_MODELS (state, data) {
      _.set(state, 'knowledgeBases', data)
    },

    SET_LIMITS (state, data) {
      _.set(state, `limits.${data.path}`, data.response)
    },

    UPDATE_KNOWLEDGEBASE (state, { id, updatedModel }) {
      state.knowledgeBases = state.knowledgeBases.map((knowledgeBase) => {
        if (knowledgeBase.id !== id) {
          return knowledgeBase
        }
        return { ...knowledgeBase, ...updatedModel }
      })
    },

    SET_MODEL_ID (state, id) {
      _.set(state, 'current', id)
    },

    SET_ALL_APPLICATIONS (state, value) {
      state.allApplications = value
    },

    SET_CATEGORIES (state, {
      loading = false,
      value = state.categories.value,
      last = state.categories.last,
      isTruncated = true
    }) {
      return _.set(state, 'categories', {
        loading, value, last, isTruncated
      })
    },

    SET_INTENTS (state, { loading = false, value = [] }) {
      return _.set(state, 'intents', { loading, value })
    },

    SET_MIGRATE_BTN_PROCESSING (state, value) {
      state.migrateBtnProcessing = value
    }
  }
})

export default store
